<template>
    <Panel class="health-checks-panel">
        <template #header>
            <div class="panel-header-row">
                <span class="panel-title"><i class="pi pi-list-check"></i> Health Checks</span>
                <span class="panel-header-actions">
                    <span v-if="lastUpdatedDate" v-tooltip.left="lastUpdatedTooltip" class="health-updated"
                        >{{ compactDuration(lastUpdatedDate, now) }} ago</span
                    >
                    <Button
                        icon="pi pi-refresh"
                        size="small"
                        severity="secondary"
                        text
                        rounded
                        :loading="refreshing"
                        aria-label="Refresh health checks"
                        @click="refresh"
                    />
                </span>
            </div>
        </template>

        <div v-if="checks.length" class="health-list">
            <div class="health-row health-head" aria-hidden="true">
                <span class="health-label"></span>
                <span class="health-expected">Expected</span>
                <span class="health-current">Current</span>
            </div>

            <!-- The whole row carries the tooltip so the explanation is easy to reach. -->
            <div
                v-for="check of checks"
                :key="check.key"
                v-tooltip.left="check.tooltip"
                class="health-row"
                :class="[`severity-${check.severity}`, { 'is-alert': check.alert, 'has-tooltip': check.tooltip }]"
            >
                <span class="health-label">
                    <i v-if="check.alert" class="pi pi-exclamation-triangle health-alert-icon" aria-hidden="true"></i>
                    {{ check.label }}
                    <span v-if="check.alert" class="sr-only">— needs attention</span>
                </span>
                <span class="health-expected"><span class="col-hint">Expected</span>{{ check.expected }}</span>
                <span class="health-current"><span class="col-hint">Current</span>{{ check.current }}</span>
            </div>
        </div>
        <div v-else class="health-empty">
            <i class="pi pi-info-circle"></i>
            Nothing to check yet
        </div>
    </Panel>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { ReadingStatuses, type AccountVesselV3, type ReadingsV1, type VesselProtocolInfoV1 } from '@geckoal/gecko-api-client'
import Panel from 'primevue/panel'
import Button from 'primevue/button'
import { useApi } from '@/api'
import { formatDateForVesselTimezone, handleApiError } from '@/lib/utils'
import { toReadingsArray } from '@/lib/readings'
import {
    checkFromSpaPair,
    compactDuration,
    expectedRangeForReadingType,
    formatReadingValue,
    mergeCurrentReadings,
    severityForReadingStatus,
    HEALTH_CHECK_READING_ORDER,
    type HealthCheck,
    type HealthCheckSeverity,
    type SpaHealthChecks
} from '@/lib/vesselHealth'

const WATER_REPORT_OK_DAYS = 7
const WATER_REPORT_WARN_DAYS = 14

const props = defineProps<{
    accountId: number
    vesselId: number
    vessel?: AccountVesselV3
    vesselTimezone?: string
    /** Live spa expectations emitted by SpaStateValidator. */
    spaHealth?: SpaHealthChecks
}>()

const emit = defineEmits<{
    /** Asks the parent to reload the vessel so the checks reflect fresh status data. */
    refresh: []
}>()

const protocolInfo = ref<VesselProtocolInfoV1 | undefined>()
const refreshing = ref(false)
const now = ref(new Date())

let ticker: ReturnType<typeof setInterval> | undefined

const status = computed(() => props.vessel?.status)
const hasMonitor = computed(() => props.vessel?.monitor?.monitorId !== undefined)
const lastUpdatedDate = computed(() => status.value?.dates?.lastStatusUpdatedDate)

const lastUpdatedTooltip = computed(() => {
    if (lastUpdatedDate.value === undefined) return ''
    return `Status last updated ${formatDateForVesselTimezone({ date: lastUpdatedDate.value, vesselTimezoneId: props.vesselTimezone })}`
})

const minutesSince = (date: Date): number => (now.value.getTime() - date.getTime()) / 60000

const severityForAge = (date: Date | undefined, okMinutes: number, warnMinutes: number): HealthCheckSeverity => {
    if (date === undefined) return 'danger'
    const minutes = minutesSince(date)
    if (minutes <= okMinutes) return 'ok'
    if (minutes <= warnMinutes) return 'warn'
    return 'danger'
}

/** Water chemistry and temperature, compared against the protocol's in-range band. */
const readingChecks = computed((): HealthCheck[] => {
    const readings = mergeCurrentReadings(status.value)

    return HEALTH_CHECK_READING_ORDER.reduce<HealthCheck[]>((list, readingType) => {
        const reading = readings[readingType as keyof ReadingsV1]
        if (reading === undefined) return list

        const expected = expectedRangeForReadingType(readingType, protocolInfo.value)

        // Nothing to check: the protocol defines no target band for this reading and
        // the reading itself carries no status, so it would only be a row of dashes.
        if (expected === undefined && reading.status === ReadingStatuses.Unknown) return list

        list.push({
            key: readingType,
            label: reading.title || reading.abbreviation || readingType,
            expected: expected ?? '—',
            current: formatReadingValue(reading),
            severity: severityForReadingStatus(reading.status),
            tooltip: reading.statusReason !== undefined ? `Reported ${compactDuration(reading.date, now.value)} ago — ${reading.statusReason}` : undefined
        })
        return list
    }, [])
})

/**
 * Driven by the module's reported cloud connection state. The last check-in time is
 * kept as supporting context in the tooltip rather than being the check itself.
 */
const moduleOnlineCheck = computed((): HealthCheck => {
    const check = checkFromSpaPair('moduleOnline', 'Module online', props.spaHealth?.moduleOnline, props.spaHealth)

    const lastCheckin = status.value?.dates?.lastMonitorCheckinDate
    if (lastCheckin === undefined) return check

    const seen = `Last checked in ${compactDuration(lastCheckin, now.value)} ago (${formatDateForVesselTimezone({
        date: lastCheckin,
        vesselTimezoneId: props.vesselTimezone
    })}).`

    return { ...check, tooltip: check.tooltip !== undefined ? `${check.tooltip} ${seen}` : seen }
})

const waterReportCheck = computed((): HealthCheck => {
    const dates = toReadingsArray(status.value?.reportReadings)
        .map((reading) => reading.date)
        .filter((date): date is Date => date !== undefined)

    const latest = dates.length > 0 ? new Date(Math.max(...dates.map((date) => date.getTime()))) : undefined

    return {
        key: 'waterReportAge',
        label: 'Water report age',
        expected: `< ${WATER_REPORT_OK_DAYS} days`,
        current: latest !== undefined ? compactDuration(latest, now.value) : 'Never',
        severity: severityForAge(latest, WATER_REPORT_OK_DAYS * 24 * 60, WATER_REPORT_WARN_DAYS * 24 * 60),
        tooltip:
            latest !== undefined
                ? `Most recent water report reading ${formatDateForVesselTimezone({ date: latest, vesselTimezoneId: props.vesselTimezone })}`
                : 'No water report readings on file.'
    }
})

const checks = computed((): HealthCheck[] => {
    const list = [...readingChecks.value]

    if (hasMonitor.value) {
        list.push(checkFromSpaPair('operationMode', 'Operation mode', props.spaHealth?.operationMode, props.spaHealth))
        list.push(checkFromSpaPair('ecoSchedule', 'Eco schedule', props.spaHealth?.ecoSchedule, props.spaHealth))
        list.push(checkFromSpaPair('filtration', 'Filtration', props.spaHealth?.filtration, props.spaHealth))

        // Only present while a smart routine session is targeting a temperature.
        if (props.spaHealth?.heaterSetPoint !== undefined) {
            list.push(checkFromSpaPair('heaterSetPoint', 'Heater setpoint', props.spaHealth.heaterSetPoint, props.spaHealth))
        }

        list.push(checkFromSpaPair('rfSignal', 'Signal', props.spaHealth?.rfSignal, props.spaHealth))
        list.push(moduleOnlineCheck.value)
    }

    list.push(waterReportCheck.value)

    return list
})

const loadProtocolInfo = async () => {
    await handleApiError(async () => {
        protocolInfo.value = await useApi().getVesselProtocolInfoV1({ accountId: props.accountId, vesselId: props.vesselId })
    })
}

const refresh = async () => {
    refreshing.value = true
    emit('refresh')
    await loadProtocolInfo()
    now.value = new Date()
    refreshing.value = false
}

onMounted(() => {
    loadProtocolInfo()
    ticker = setInterval(() => (now.value = new Date()), 30000)
})

onUnmounted(() => {
    if (ticker !== undefined) clearInterval(ticker)
})

watch([() => props.accountId, () => props.vesselId], () => {
    protocolInfo.value = undefined
    loadProtocolInfo()
})
</script>

<style lang="scss" scoped>
:deep(.p-panel-content) {
    padding: 4px 16px 8px;
}

:deep(.p-panel-header) {
    padding: 8px 16px;
}

.health-checks-panel {
    height: 100%;
}

.panel-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 8px;
}

.panel-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #444;
}

.panel-header-actions {
    display: flex;
    align-items: center;
    gap: 2px;
}

.health-updated {
    font-size: 0.72rem;
    color: #999;
    cursor: help;
    white-space: nowrap;
}

.health-list {
    display: flex;
    flex-direction: column;
}

/*
 * Column headings, so it is obvious which number is the target and which is live.
 * Hidden on narrow screens, where rows stack and the inline hints take over.
 */
.health-head {
    padding: 2px 0 4px;
    border-bottom: 1px solid #e0e0e0;

    .health-expected,
    .health-current {
        font-size: 0.65rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        color: #aaa;
    }

    @media (max-width: 600px) {
        display: none;
    }
}

/* Inline column labels for the stacked mobile layout. */
.col-hint {
    display: none;
}

@media (max-width: 600px) {
    .col-hint {
        display: inline;
        margin-right: 4px;
        font-size: 0.62rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #bbb;
    }
}

.health-row {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 9px 0;
    border-bottom: 1px solid #eee;

    &:last-child {
        border-bottom: none;
    }

    &.has-tooltip {
        cursor: help;
    }

    @media (max-width: 600px) {
        flex-wrap: wrap;
        gap: 4px 10px;
    }
}

/*
 * A fault the vessel should never be in — bleed the highlight to the panel edges
 * and pulse it so it reads as "act on this", not just "value is off".
 */
.health-row.is-alert {
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 13px;
    padding-right: 16px;
    border-left: 3px solid #c62828;
    border-radius: 2px;
    animation: health-alert-pulse 2.4s ease-in-out infinite;
}

@keyframes health-alert-pulse {
    0%,
    100% {
        background: #fff5f5;
        box-shadow: inset 0 0 0 rgba(198, 40, 40, 0);
    }
    50% {
        background: #ffe9e9;
        box-shadow: inset 0 0 14px rgba(198, 40, 40, 0.22);
    }
}

@media (prefers-reduced-motion: reduce) {
    .health-row.is-alert {
        animation: none;
        background: #ffe9e9;
    }
}

.health-label {
    flex: 1;
    min-width: 0;
    font-size: 0.85rem;
    color: #333;

    @media (max-width: 600px) {
        flex: 1 0 100%;
        font-weight: 600;
    }
}

.health-alert-icon {
    font-size: 0.75rem;
    color: #c62828;
    margin-right: 2px;
}

/* Visually hidden, still announced by screen readers. */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.health-expected {
    font-size: 0.8rem;
    color: #9e9e9e;
    text-align: right;
    white-space: nowrap;

    @media (max-width: 600px) {
        flex: 1;
        text-align: left;
    }
}

.health-current {
    font-size: 0.85rem;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
    min-width: 68px;
}

.severity-ok .health-current {
    color: #2e7d32;
}

.severity-warn .health-current {
    color: #ef6c00;
}

.severity-danger .health-current {
    color: #c62828;
}

.severity-unknown .health-current {
    color: #9e9e9e;
    font-weight: 500;
}

.health-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0;
    font-size: 0.85rem;
    color: #888;
}
</style>
